import { create } from 'zustand'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

const PREF_QUERY_KEY = ['me-preferences', 'product-tour']

/**
 * Transient UI state for the running tour (phase + which step). It is NOT the
 * source of truth — persistence lives server-side in `productTour.state` via
 * /me/preferences. Kept in a tiny store so the "Take Product Tour" menu items
 * can trigger it from anywhere without prop-drilling.
 */
export const useTourStore = create((set) => ({
  phase: 'idle', // 'idle' | 'welcome' | 'running'
  // While the tour runs, the sidebar group/section the CURRENT step lives in
  // (e.g. 'administration', 'process', 'supervision', 'reports') — or null for a
  // top-level step. The layouts open ONLY this group (accordion: others close);
  // at tour start it's null so every dropdown is closed.
  activeGroup: null,
  openWelcome: () => set({ phase: 'welcome', activeGroup: null }),
  startRunning: () => set({ phase: 'running', activeGroup: null }),
  close: () => set({ phase: 'idle', activeGroup: null }),
  setActiveGroup: (activeGroup) => set({ activeGroup }),
}))

/** Imperative entry point for the "Take Product Tour" actions (UserMenu, User
 *  Manual). A deliberate restart skips the welcome and runs the tour directly. */
export function startProductTour() {
  useTourStore.getState().startRunning()
}

/** The signed-in user's persisted tour state (`{ state, lastStepIndex }` or
 *  null when they predate the feature). Fetched from /me/preferences; disabled
 *  for unsupported users so it never fires for the Super Admin console. */
export function useTourPreference(enabled) {
  return useQuery({
    queryKey: PREF_QUERY_KEY,
    enabled: Boolean(enabled),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await api.get('/me/preferences')
      return data?.productTour ?? null
    },
  })
}

/**
 * Persist a tour state transition (not_started → in_progress / skipped /
 * completed). A partial PUT — the backend writes only the `productTour` slice,
 * so favourites/hidden are untouched. Best-effort: a failed write must never
 * trap the user in the overlay, so callers close the UI regardless.
 */
export function useSetTourState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ state, lastStepIndex }) => {
      const productTour = { state }
      if (typeof lastStepIndex === 'number') productTour.lastStepIndex = lastStepIndex
      const { data } = await api.put('/me/preferences', { productTour })
      return data?.productTour ?? { state, lastStepIndex: lastStepIndex ?? 0 }
    },
    onSuccess: (productTour) => qc.setQueryData(PREF_QUERY_KEY, productTour),
  })
}

/**
 * Persist the "show the tour on login" preference (`productTour.autoShow`).
 * A partial PUT — the backend writes ONLY `autoShow` (dot notation), so the
 * tour's `state` / `lastStepIndex` are untouched. Off = the tour stops
 * auto-opening on each login (the user can still start it from "Take Product
 * Tour"). Optimistic so the switch flips instantly, with rollback on error.
 */
export function useSetTourAutoShow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (autoShow) => {
      const { data } = await api.put('/me/preferences', {
        productTour: { autoShow },
      })
      return data?.productTour ?? { autoShow }
    },
    onMutate: async (autoShow) => {
      await qc.cancelQueries({ queryKey: PREF_QUERY_KEY })
      const prev = qc.getQueryData(PREF_QUERY_KEY)
      qc.setQueryData(PREF_QUERY_KEY, (p) => ({ ...(p ?? {}), autoShow }))
      return { prev }
    },
    onError: (_err, _autoShow, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(PREF_QUERY_KEY, ctx.prev)
    },
    onSuccess: (productTour) => qc.setQueryData(PREF_QUERY_KEY, productTour),
  })
}
